/**
 * Hand-authored, CSP-safe SVG diagrams for the findings-lifecycle
 * learn guide.
 *
 * Why SVG-by-hand and not Mermaid / Excalidraw runtime renderers:
 *   - The web app's CSP forbids inline `<style>` blocks AND inline
 *     `style="..."` attributes (`style-src 'self'` and
 *     `style-src-attr 'none'`). Both Mermaid and Excalidraw's runtime
 *     pull-in heavy CSS that needs one or the other to render edges.
 *   - Authoring SVG by hand means *all* presentation goes onto SVG
 *     attributes (`fill="..."`, `stroke="..."`) which CSP doesn't
 *     touch. No JS dependency, no runtime parsing, no surprises.
 *
 * Theme handling: stroke + node fills are saturated colours that read
 * on both light and dark backgrounds; text uses `currentColor` so it
 * inherits the page foreground (DM Sans, oklch). Result: one diagram
 * works in both modes.
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

// ─── Shared atoms ───────────────────────────────────────────────────────────

interface NodeProps {
  x: number;
  y: number;
  w?: number;
  h?: number;
  fill: string;       // pastel pill fill
  stroke: string;     // stronger ring + label colour
  label: string;
  fontSize?: number;
}

function Node({ x, y, w = 140, h = 60, fill, stroke, label, fontSize = 18 }: NodeProps) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={12} ry={12} fill={fill} stroke={stroke} strokeWidth={2} />
      <text
        x={x + w / 2}
        y={y + h / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="DM Sans, system-ui, sans-serif"
        fontSize={fontSize}
        fontWeight={600}
        fill={stroke}
      >
        {label}
      </text>
    </g>
  );
}

interface DiamondProps {
  cx: number; cy: number; w?: number; h?: number;
  fill: string; stroke: string; label?: string; lines?: string[]; fontSize?: number;
}

function Diamond({ cx, cy, w = 200, h = 100, fill, stroke, label, lines, fontSize = 14 }: DiamondProps) {
  const half = { x: w / 2, y: h / 2 };
  const points = `${cx},${cy - half.y} ${cx + half.x},${cy} ${cx},${cy + half.y} ${cx - half.x},${cy}`;
  const items = lines ?? (label ? [label] : []);
  return (
    <g>
      <polygon points={points} fill={fill} stroke={stroke} strokeWidth={2} />
      {items.map((line, i) => (
        <text
          key={i}
          x={cx}
          y={cy + (i - (items.length - 1) / 2) * (fontSize + 3)}
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="DM Sans, system-ui, sans-serif"
          fontSize={fontSize}
          fontWeight={600}
          fill={stroke}
        >
          {line}
        </text>
      ))}
    </g>
  );
}

interface ArrowProps {
  d: string;
  color?: string;
  dashed?: boolean;
  label?: string;
  labelX?: number;
  labelY?: number;
  labelLines?: string[];
  markerId?: string;
}

function Arrow({ d, color = 'currentColor', dashed, label, labelX, labelY, labelLines, markerId = 'arrow' }: ArrowProps) {
  const items = labelLines ?? (label ? [label] : []);
  return (
    <g>
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={1.75}
        strokeDasharray={dashed ? '6 4' : undefined}
        markerEnd={`url(#${markerId})`}
      />
      {items.length > 0 && labelX !== undefined && labelY !== undefined && (
        <g>
          {items.map((line, i) => (
            <text
              key={i}
              x={labelX}
              y={labelY + (i - (items.length - 1) / 2) * 14}
              textAnchor="middle"
              dominantBaseline="central"
              fontFamily="DM Sans, system-ui, sans-serif"
              fontSize={12}
              fill={color}
            >
              {line}
            </text>
          ))}
        </g>
      )}
    </g>
  );
}

interface DiagramFrameProps {
  viewBox: string;
  className?: string;
  children: ReactNode;
  caption?: string;
}

function DiagramFrame({ viewBox, className, children, caption }: DiagramFrameProps) {
  return (
    <figure className={cn('my-4', className)}>
      <div className="rounded-lg border border-border bg-card p-4">
        <svg
          viewBox={viewBox}
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-auto block"
          role="img"
          fontFamily="DM Sans, system-ui, sans-serif"
        >
          <defs>
            {/* Filled-triangle arrowheads in three colours so we can
                colour-code arrow lines (success, regression, neutral)
                without needing inline styles. */}
            <marker id="arrow-neutral" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
            </marker>
            <marker id="arrow-success" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#15803d" />
            </marker>
            <marker id="arrow-regression" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#dc2626" />
            </marker>
            <marker id="arrow-warn" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#b45309" />
            </marker>
          </defs>
          {children}
        </svg>
      </div>
      {caption && (
        <figcaption className="text-[11px] text-muted-foreground text-center mt-2">{caption}</figcaption>
      )}
    </figure>
  );
}

// ─── 1. Lifecycle state diagram ─────────────────────────────────────────────

export function FindingLifecycleDiagram() {
  // Layout zones (top to bottom):
  //   y 20–80   regression arc labels live here
  //   y 90–145  the regression arcs themselves swoop up over this band
  //   y 220     centre-line of the top row (forward states)
  //   y 380     centre-line of the bottom row (terminal user-driven states)
  return (
    <DiagramFrame
      viewBox="0 0 880 520"
      caption="Trinetra finding lifecycle. Solid = forward transitions. Dashed red = regression (bug came back)."
    >
      {/* Start dot — labelled below so the arrow stays clean */}
      <circle cx={50} cy={220} r={8} fill="currentColor" />
      <text x={50} y={245} textAnchor="middle" fontSize={11} fill="currentColor" opacity={0.7} fontFamily="DM Sans, system-ui, sans-serif">
        report_finding
      </text>

      {/* Top row — forward states */}
      <Node x={140} y={190} label="open" fill="#fee2e2" stroke="#dc2626" />
      <Node x={400} y={190} w={150} label="fix-merged" fill="#dcfce7" stroke="#16a34a" />
      <Node x={620} y={190} label="fixed" fill="#bbf7d0" stroke="#15803d" />

      {/* Bottom row — terminal states */}
      <Node x={140} y={380} label="dismissed" fill="#e5e7eb" stroke="#525252" fontSize={16} />
      <Node x={380} y={380} w={170} label="accepted-risk" fill="#fef3c7" stroke="#b45309" fontSize={16} />

      {/* ── Forward arrows ── */}
      <Arrow d="M 60 220 L 138 220" color="currentColor" markerId="arrow-neutral" />
      <Arrow
        d="M 280 220 L 398 220"
        color="#15803d"
        markerId="arrow-success"
        label="PR merged"
        labelX={339}
        labelY={208}
      />
      <Arrow
        d="M 550 220 L 618 220"
        color="#15803d"
        markerId="arrow-success"
        labelLines={['next scan:', 'gone']}
        labelX={584}
        labelY={198}
      />

      {/* ── Regression arcs — both swoop UP, well above the top row ── */}
      {/* arc 1 — fix-merged → open, peaks at y ≈ 110 */}
      <Arrow
        d="M 460 188 C 460 110, 220 110, 210 188"
        color="#dc2626"
        dashed
        markerId="arrow-regression"
        label="REGRESSION (fix-merged)"
        labelX={335}
        labelY={75}
      />
      {/* arc 2 — fixed → open, much wider arc, peaks at y ≈ 50 */}
      <Arrow
        d="M 670 188 C 670 50, 200 50, 200 188"
        color="#dc2626"
        dashed
        markerId="arrow-regression"
        label="REGRESSION (fixed)"
        labelX={435}
        labelY={32}
      />

      {/* ── Open → dismissed / accepted-risk (clean lower half) ── */}
      <Arrow
        d="M 195 250 L 195 380"
        color="#525252"
        markerId="arrow-neutral"
        label="user dismisses"
        labelX={257}
        labelY={310}
      />
      <Arrow
        d="M 250 250 L 420 380"
        color="#b45309"
        markerId="arrow-warn"
        label="user accepts risk"
        labelX={395}
        labelY={290}
      />

      {/* Self-loop note */}
      <g>
        <text x={720} y={390} fontSize={12} fill="currentColor" opacity={0.85} fontFamily="DM Sans, system-ui, sans-serif" fontWeight={600}>
          Re-seen on next scan?
        </text>
        <text x={720} y={410} fontSize={11} fill="currentColor" opacity={0.7} fontFamily="DM Sans, system-ui, sans-serif">
          <tspan x={720} dy={0}>lastSeenAt bumped,</tspan>
          <tspan x={720} dy={14}>status unchanged.</tspan>
        </text>
      </g>
    </DiagramFrame>
  );
}

// ─── 2. Dedup decision tree ────────────────────────────────────────────────

export function DedupDecisionDiagram() {
  return (
    <DiagramFrame
      viewBox="0 0 880 660"
      caption="The decision tree the agent-engine harness runs every time it flushes findings to the DB."
    >
      <Node x={340} y={20} w={200} label="Agent emits report_finding" fill="#dbeafe" stroke="#2563eb" fontSize={15} />
      <Node x={340} y={110} w={200} label="Compute SARIF fingerprints" fill="#ede9fe" stroke="#7c3aed" fontSize={14} />

      <Diamond
        cx={440} cy={245}
        w={280} h={100}
        fill="#fef3c7" stroke="#b45309"
        lines={['dedup_key', '(= contextRegionHash)', 'present?']}
        fontSize={13}
      />

      <Node x={50} y={310} w={220} label="Insert as new (no dedup)" fill="#dcfce7" stroke="#16a34a" fontSize={14} />

      <Diamond
        cx={440} cy={420}
        w={320} h={110}
        fill="#fef3c7" stroke="#b45309"
        lines={['Existing finding with same', '(projectId, dedup_key)?']}
        fontSize={14}
      />

      <Node x={50} y={485} w={200} label="Insert new" fill="#dcfce7" stroke="#16a34a" fontSize={14} />

      {/* Outcome boxes — taller, two-line labels so the qualifier sits inside the pill instead of overlapping below */}
      <g>
        <rect x={120} y={580} width={220} height={68} rx={12} ry={12} fill="#dbeafe" stroke="#2563eb" strokeWidth={2} />
        <text x={230} y={603} textAnchor="middle" dominantBaseline="central" fontSize={14} fontWeight={600} fill="#2563eb" fontFamily="DM Sans, system-ui, sans-serif">UPDATE lastSeenAt</text>
        <text x={230} y={628} textAnchor="middle" dominantBaseline="central" fontSize={11} fill="#2563eb" opacity={0.85} fontFamily="DM Sans, system-ui, sans-serif">when status = open / dismissed</text>
      </g>

      <g>
        <rect x={530} y={580} width={260} height={68} rx={12} ry={12} fill="#fee2e2" stroke="#dc2626" strokeWidth={2} />
        <text x={660} y={603} textAnchor="middle" dominantBaseline="central" fontSize={14} fontWeight={600} fill="#dc2626" fontFamily="DM Sans, system-ui, sans-serif">REOPEN — flip status=open</text>
        <text x={660} y={628} textAnchor="middle" dominantBaseline="central" fontSize={11} fill="#dc2626" opacity={0.85} fontFamily="DM Sans, system-ui, sans-serif">when status = fixed / fix-merged (REGRESSION)</text>
      </g>

      {/* Arrows */}
      <Arrow d="M 440 60 L 440 108" color="currentColor" markerId="arrow-neutral" />
      <Arrow d="M 440 150 L 440 197" color="currentColor" markerId="arrow-neutral" />

      <Arrow d="M 305 245 L 270 310" color="#16a34a" markerId="arrow-success" label="no" labelX={278} labelY={272} />
      <Arrow d="M 440 295 L 440 367" color="currentColor" markerId="arrow-neutral" label="yes" labelX={458} labelY={325} />

      <Arrow d="M 295 420 L 250 488" color="#16a34a" markerId="arrow-success" label="no match" labelX={240} labelY={448} />
      <Arrow d="M 380 472 L 230 580" color="#2563eb" markerId="arrow-neutral" />
      <Arrow d="M 510 472 L 600 580" color="#dc2626" markerId="arrow-regression" />
    </DiagramFrame>
  );
}

// ─── 3. PR dedup flow ───────────────────────────────────────────────────────

export function PrDedupDiagram() {
  return (
    <DiagramFrame
      viewBox="0 0 820 380"
      caption="Same fingerprint → same branchSuffix → same branch reused. Re-runs update the existing PR instead of opening a new one."
    >
      <Node x={20} y={150} w={180} h={80} label="Skill calls open_fix_pr" fill="#ede9fe" stroke="#7c3aed" fontSize={14} />

      {/* branchSuffix box — manual 2-line layout instead of <Node> so the
          slug template (small) sits below the title (bold) inside the
          same pill. */}
      <g>
        <rect x={250} y={150} width={220} height={80} rx={12} ry={12} fill="#dbeafe" stroke="#2563eb" strokeWidth={2} />
        <text x={360} y={180} textAnchor="middle" dominantBaseline="central" fontSize={15} fontWeight={600} fill="#2563eb" fontFamily="DM Sans, system-ui, sans-serif">branchSuffix</text>
        <text x={360} y={205} textAnchor="middle" dominantBaseline="central" fontSize={11} fill="#2563eb" opacity={0.85} fontFamily="DM Sans, system-ui, sans-serif">ruleSlug-contextRegionHash</text>
      </g>

      <Diamond
        cx={620} cy={190}
        w={240} h={110}
        fill="#fef3c7" stroke="#b45309"
        lines={['Branch trinetra/<suffix>', 'exists on repo?']}
        fontSize={14}
      />

      <Node x={250} y={310} w={220} h={56} label="Reuse branch · update PR" fill="#dcfce7" stroke="#15803d" fontSize={13} />
      <Node x={580} y={310} w={220} h={56} label="Create branch · open PR" fill="#dcfce7" stroke="#16a34a" fontSize={13} />

      {/* Arrows */}
      <Arrow d="M 200 190 L 248 190" color="currentColor" markerId="arrow-neutral" />
      <Arrow d="M 470 190 L 520 190" color="currentColor" markerId="arrow-neutral" />

      <Arrow d="M 580 235 L 380 308" color="#15803d" markerId="arrow-success" label="yes — same fingerprint" labelX={420} labelY={275} />
      <Arrow d="M 660 235 L 690 308" color="#16a34a" markerId="arrow-success" label="no" labelX={714} labelY={275} />
    </DiagramFrame>
  );
}
